// =============================================================================
// Layer 3 — arbitration rules.
//
// Takes the list of patterns that fired at Layer 2 and applies the 6 priority-
// ordered rules from decision-logic.yaml to pick a single winner (or explicitly
// name the ambiguity as mixed_picture / insufficient_data).
//
// Priority order:
//   1. urgent severity flags (Hb <7, postpartum <8, ferritin>1000+TSAT>50)
//      — set severity='urgent'; do NOT change pattern classification itself
//   2. inflammation caveat — set flag; demote confidence on ferritin patterns
//   3. overload_supersedes_deficiency — suppress stage patterns if overload fired
//   4. acd_supersedes_functional_id_when_anemic — suppress FID when ACD fired
//   5. life_stage_threshold_application — applied in Layer 1 already
//   6. confidence_tier_by_input_completeness — reduce confidence per tier
//
// After suppressions, if 0 patterns remain → insufficient_data;
//                    if 1 pattern remains → that's the winner;
//                    if 2+ remain → mixed_picture + list competitors.
// =============================================================================

import {
  detectCompleteness,
  isFerritinCaveatFlagged,
  isInflammationFlagged,
  isIronSupplementationFlagged,
} from './classify.js';
import type { PatternId } from './patterns.js';
import { buildSecondaryCandidate } from './secondary-candidates.js';
import type { ThresholdEvaluation } from './thresholds.js';
import type {
  CompletenessTier,
  ConfidenceTier,
  LabInputs,
  SecondaryCandidate,
  SeverityTier,
} from './types.js';

/** All patterns including the meta-patterns synthesized by this layer. */
export type WinnerPatternId =
  | PatternId
  | 'mixed_picture_multiple_patterns'
  | 'insufficient_data'
  | 'no_established_pattern'
  | 'severity_urgent_override';

/** Full output of Layer 3. */
export interface ArbitrationResult {
  /** Final pattern classification. */
  winner: WinnerPatternId;

  /** Severity banner tier. Independent of winner — urgent can ride on top of any pattern. */
  severity: SeverityTier;

  /** True when ferritin interpretation is flagged due to inflammation (CRP ≥ 5 or recent illness). */
  inflammationCaveat: boolean;

  /** True when ferritin interpretation is flagged due to current or recent (≤ 2 week) iron supplementation. */
  supplementationCaveat: boolean;

  /** Confidence tier for the final classification. */
  confidenceTier: ConfidenceTier;

  /** Patterns that were suppressed during arbitration + the rule that suppressed them. */
  suppressed: Array<{ patternId: PatternId; rule: string }>;

  /** Audit trail — ordered list of arbitration rules that fired. */
  arbitrationTrace: string[];

  /** Secondary candidates surfaced when winner is mixed_picture. */
  secondaryCandidates?: SecondaryCandidate[];

  /** Completeness tier of the input set — drives confidence + what outputs are available. */
  completeness: CompletenessTier;
}

// -----------------------------------------------------------------------------
// Severity detection — priority 1 rules
// -----------------------------------------------------------------------------

/**
 * Compute severity tier from the inputs + threshold evaluation.
 * Urgent triggers (any of):
 *   - Hb < 70 g/L (< 7.0 g/dL) — severe anemia
 *   - Postpartum + Hb < 80 g/L (< 8.0 g/dL)
 *   - Ferritin > 1000 AND TSAT > 50 — prompt overload evaluation
 * Prompt triggers:
 *   - Any overload pattern fired
 *   - ACD / functional ID patterns
 * Routine:
 *   - Everything else
 */
export function computeSeverity(
  firedPatterns: PatternId[],
  evaluation: ThresholdEvaluation,
  inputs: LabInputs,
): SeverityTier {
  // Priority 1a: severe anemia Hb < 70 g/L
  if (evaluation.hemoglobinZone === 'severe_anemia') return 'urgent';
  if (evaluation.hemoglobinZone === 'transfusion_territory') return 'urgent';

  // Priority 1b: postpartum Hb < 80 g/L
  if (evaluation.hemoglobinZone === 'severe_postpartum') return 'urgent';

  // Priority 1c: ferritin > 1000 combined with TSAT > 50
  if (
    inputs.ferritin !== undefined &&
    inputs.ferritin > 1000 &&
    inputs.tsat !== undefined &&
    inputs.tsat > 50
  ) {
    return 'urgent';
  }

  // Prompt tier: overload patterns + ACD + functional ID all warrant
  // clinician attention sooner than routine retest cadence.
  const promptPatterns: PatternId[] = [
    'iron_overload_trigger_premenopausal',
    'iron_overload_trigger_postmenopausal',
    'very_high_ferritin_any_stage',
    'anemia_of_chronic_disease_classic',
    'functional_iron_deficiency',
    'stage_4_iron_deficiency_anemia',
    'macrocytic_anemia_signal',
    'anemia_with_replete_ferritin',
  ];
  if (firedPatterns.some((id) => promptPatterns.includes(id))) return 'prompt';

  return 'routine';
}

// -----------------------------------------------------------------------------
// Suppression rules
// -----------------------------------------------------------------------------

/**
 * Priority-3 rule: overload_supersedes_deficiency.
 * When any overload pattern fired, suppress the iron-deficiency staged-model
 * patterns — different clinical pathway, don't cross-classify.
 */
function applyOverloadSupersession(
  firedPatterns: PatternId[],
): {
  survivors: PatternId[];
  suppressed: Array<{ patternId: PatternId; rule: string }>;
  traceFired: boolean;
} {
  const overloadPatterns: PatternId[] = [
    'iron_overload_trigger_premenopausal',
    'iron_overload_trigger_postmenopausal',
    'very_high_ferritin_any_stage',
  ];
  const overloadFired = firedPatterns.some((id) => overloadPatterns.includes(id));
  if (!overloadFired) return { survivors: firedPatterns, suppressed: [], traceFired: false };

  // Overload supersedes:
  //   - Staged iron-deficiency model (stage_0..4)
  //   - Inflammation-context iron patterns (FID, ACD) — the iron findings
  //     are separately interpretable but the overload signal is the
  //     actionable one and the right clinical pathway
  //   - Morphology patterns (microcytic_normal_iron_studies,
  //     macrocytic_anemia_signal) — overload is more urgent and
  //     subsumes the morphology routing
  //   - anemia_with_replete_ferritin — when overload is present, the
  //     "ferritin doesn't explain anemia" framing is wrong (overload
  //     ferritin level explains it differently)
  const deficiencyToSuppress: PatternId[] = [
    'stage_0_iron_replete',
    'stage_1_incipient_id',
    'stage_2_iron_depletion_no_anemia',
    'stage_3_iron_deficient_erythropoiesis',
    'stage_4_iron_deficiency_anemia',
    'functional_iron_deficiency',
    'anemia_of_chronic_disease_classic',
    'microcytic_normal_iron_studies',
    'macrocytic_anemia_signal',
    'anemia_with_replete_ferritin',
  ];

  const suppressed: Array<{ patternId: PatternId; rule: string }> = [];
  const survivors: PatternId[] = [];

  for (const id of firedPatterns) {
    if (deficiencyToSuppress.includes(id)) {
      suppressed.push({ patternId: id, rule: 'overload_supersedes_deficiency' });
    } else {
      survivors.push(id);
    }
  }

  return { survivors, suppressed, traceFired: true };
}

/**
 * Priority-3 rule: morphology_supersedes_iron_replete_when_microcytic.
 *
 * microcytic_normal_iron_studies fires when MCV is microcytic AND ferritin
 * is at or above the depletion threshold (>= 30) AND TSAT is not low.
 * Stage 0 (replete) and Stage 1 (incipient) iron-staging patterns can fire
 * for the same user (their gates are non-anemic Hb + ferritin in their
 * respective zones). Both firing produces a mixed_picture, but clinically
 * the morphology read is more actionable: "your iron is fine but your red
 * cells are small — get hemoglobin electrophoresis to evaluate for
 * thalassemia trait or other hemoglobinopathy" is a specific clinical
 * pathway, while "your iron is fine" loses signal.
 *
 * Evidence: morphology-based hemoglobinopathy screening is the standard
 * differential when iron studies don't explain microcytosis (Mentzer index
 * literature; van Zeben 1990; thalassemia-screening guidelines). The
 * morphology read is the actionable signal; the staging read is the
 * incidental finding.
 *
 * Stage 2 / Stage 3 / Stage 4 are NOT suppressed here — those gate on
 * ferritin < 30, which conflicts with microcytic-normal-iron-studies'
 * ferritin >= 30 gate, so they cannot co-fire.
 */
function applyMorphologySupersession(
  firedPatterns: PatternId[],
): {
  survivors: PatternId[];
  suppressed: Array<{ patternId: PatternId; rule: string }>;
  traceFired: boolean;
} {
  const microcyticFired = firedPatterns.includes('microcytic_normal_iron_studies');
  if (!microcyticFired) {
    return { survivors: firedPatterns, suppressed: [], traceFired: false };
  }

  const stageToSuppress: PatternId[] = ['stage_0_iron_replete', 'stage_1_incipient_id'];
  const suppressed: Array<{ patternId: PatternId; rule: string }> = [];
  const survivors: PatternId[] = [];

  for (const id of firedPatterns) {
    if (stageToSuppress.includes(id)) {
      suppressed.push({
        patternId: id,
        rule: 'morphology_supersedes_iron_replete_when_microcytic',
      });
    } else {
      survivors.push(id);
    }
  }

  return { survivors, suppressed, traceFired: suppressed.length > 0 };
}

/**
 * Priority-3 rule (v0.1.1): acd_supersedes_functional_id_when_anemic.
 * When both functional_iron_deficiency AND anemia_of_chronic_disease_classic
 * fire for the same user (overlap zone: anemic + ferritin >=100 + TSAT low +
 * inflammation), ACD classic wins. Naming the anemia is clinically more
 * specific. Functional ID remains available for inflamed-but-not-yet-anemic
 * users.
 */
function applyACDSupersession(
  firedPatterns: PatternId[],
): {
  survivors: PatternId[];
  suppressed: Array<{ patternId: PatternId; rule: string }>;
  traceFired: boolean;
} {
  const acdFired = firedPatterns.includes('anemia_of_chronic_disease_classic');
  const fidFired = firedPatterns.includes('functional_iron_deficiency');
  if (!acdFired || !fidFired) {
    return { survivors: firedPatterns, suppressed: [], traceFired: false };
  }

  return {
    survivors: firedPatterns.filter((id) => id !== 'functional_iron_deficiency'),
    suppressed: [
      {
        patternId: 'functional_iron_deficiency',
        rule: 'acd_supersedes_functional_id_when_anemic',
      },
    ],
    traceFired: true,
  };
}

/**
 * inflammation_masks_iron_status normally rides along with a substantive
 * pattern as a caveat — when the user has, say, Stage 3 plus an inflammation
 * flag, the Stage 3 pattern wins and the caveat demotes its confidence.
 *
 * BUT if inflammation is the ONLY pattern that fired (e.g., elevated CRP with
 * ferritin in the "replete" numeric range, where the stage patterns are
 * suppressed at Layer 2 by the inflammationClear gate), inflammation becomes
 * the standalone winner. That's the right outcome: the user needs the
 * "ferritin can't be read at face value" narrative, not insufficient_data.
 *
 * So: strip inflammation_masks_iron_status only when at least one other
 * substantive pattern also fired.
 */
function removeInflammationMetaPattern(
  firedPatterns: PatternId[],
): PatternId[] {
  const withoutInflammation = firedPatterns.filter(
    (id) => id !== 'inflammation_masks_iron_status',
  );
  // If other substantive patterns fired, inflammation is a caveat — strip it.
  if (withoutInflammation.length > 0) return withoutInflammation;
  // Otherwise inflammation is the standalone classification — keep it.
  return firedPatterns;
}

// -----------------------------------------------------------------------------
// Confidence tier
// -----------------------------------------------------------------------------

function computeConfidenceTier(
  winner: WinnerPatternId,
  completeness: CompletenessTier,
  ferritinCaveat: boolean,
  isInferential: boolean,
): ConfidenceTier {
  if (winner === 'insufficient_data') return 'low';
  if (winner === 'mixed_picture_multiple_patterns') return 'low';
  // no_established_pattern: full panel given but corpus does not contain a
  // named pattern matching this combination. Confidence is low by definition.
  if (winner === 'no_established_pattern') return 'low';

  // Ferritin caveat (inflammation OR recent supplementation) reduces confidence
  // for ferritin-dependent patterns.
  const ferritinDependent: WinnerPatternId[] = [
    'stage_0_iron_replete',
    'stage_1_incipient_id',
    'stage_2_iron_depletion_no_anemia',
    'stage_3_iron_deficient_erythropoiesis',
    'stage_4_iron_deficiency_anemia',
  ];
  if (ferritinCaveat && ferritinDependent.includes(winner)) {
    return 'recalibration_flag';
  }

  // Completeness tier drives base confidence for substantive classifications.
  let base: ConfidenceTier;
  switch (completeness) {
    case 'tier_3_full_panel':
      base = 'high';
      break;
    case 'tier_2_cbc_plus_ferritin':
      base = 'high'; // still high confidence for most patterns
      break;
    case 'tier_1_cbc_only':
      base = 'moderate'; // can classify morphology + severity, not iron stage
      break;
  }

  // Inferential winner: the pattern fired through a defensible-but-not-
  // corpus-mandated path (e.g., inflammation_masks as standalone winner
  // when no substantive pattern caught; macrocytic_anemia_signal with
  // conflicting low-TSAT or deficient-ferritin signals; anemia_with_
  // replete_ferritin riding the microcytic-with-low-TSAT routing; the
  // Stage 4 IDA Fallback C ferritin-lag path). The literature supports
  // these calls without prescribing them — drop confidence one tier so
  // the user-facing copy reflects the inferential nature.
  if (isInferential) {
    if (base === 'high') return 'moderate';
    if (base === 'moderate') return 'low';
  }
  return base;
}

/**
 * Detect when the resolved winner reflects an engine-inferred design call
 * rather than a corpus-mandated classification. Used to downgrade confidence
 * one tier on these patterns.
 *
 * Conditions (each requires the named pattern to be the winner):
 *   - inflammation_masks_iron_status as the standalone winner (no other
 *     substantive pattern co-fired). The corpus tells us ferritin can't be
 *     read under inflammation but does not dictate this as the verdict.
 *   - macrocytic_anemia_signal when conflicting signals are present:
 *     deficient ferritin OR low TSAT OR active inflammation. The corpus
 *     supports the call but other patterns (mixed_picture, IDA, ACD,
 *     inflammation_masks) could equally fit.
 *   - anemia_with_replete_ferritin when MCV is microcytic + TSAT is low.
 *     Engine routes the conflict here as a design choice; corpus does
 *     not prescribe.
 *   - stage_4_iron_deficiency_anemia via Fallback C (borderline ferritin
 *     in stage_1_incipient zone + microcytic + low TSAT). Mei 2024 +
 *     Lahtiharju 2025 suggest this "ferritin lag" path; not a settled rule.
 */
function detectInferentialWinner(
  winner: WinnerPatternId,
  evaluation: ThresholdEvaluation,
  inputs: LabInputs,
  cofiringPatternCount: number,
): boolean {
  if (winner === 'inflammation_masks_iron_status') {
    // Standalone winner = it was the only survivor in arbitration.
    return cofiringPatternCount === 1;
  }
  if (winner === 'macrocytic_anemia_signal') {
    const ferritinDeficient =
      evaluation.ferritinZone === 'stage_2_depletion'
      || evaluation.ferritinZone === 'stage_3_ide'
      || evaluation.ferritinZone === 'stage_4_ida_range';
    const tsatLow = evaluation.tsatZone === 'low';
    const inflam = isInflammationFlagged(inputs);
    return ferritinDeficient || tsatLow || inflam;
  }
  if (winner === 'anemia_with_replete_ferritin') {
    return evaluation.mcvZone === 'microcytic' && evaluation.tsatZone === 'low';
  }
  if (winner === 'stage_4_iron_deficiency_anemia') {
    // Fallback C path: ferritin in stage_1_incipient zone (not deficient
    // by primary threshold) + microcytic MCV + low TSAT.
    return (
      evaluation.ferritinZone === 'stage_1_incipient'
      && evaluation.mcvZone === 'microcytic'
      && evaluation.tsatZone === 'low'
    );
  }
  return false;
}

// -----------------------------------------------------------------------------
// Main arbitration entry point
// -----------------------------------------------------------------------------

export function arbitrate(
  firedPatterns: PatternId[],
  evaluation: ThresholdEvaluation,
  inputs: LabInputs,
): ArbitrationResult {
  const trace: string[] = [];
  const suppressed: Array<{ patternId: PatternId; rule: string }> = [];
  const completeness = detectCompleteness(inputs);
  const inflammationCaveat = isInflammationFlagged(inputs);
  const supplementationCaveat = isIronSupplementationFlagged(inputs);
  const ferritinCaveat = isFerritinCaveatFlagged(inputs);

  // Priority 1: severity.
  const severity = computeSeverity(firedPatterns, evaluation, inputs);
  if (severity === 'urgent') trace.push('severity_urgent_detected');
  else if (severity === 'prompt') trace.push('severity_prompt_detected');

  // Priority 2: ferritin interpretation caveats.
  if (inflammationCaveat) trace.push('inflammation_caveat_applied');
  if (supplementationCaveat) trace.push('supplementation_caveat_applied');

  // Strip the inflammation meta-pattern (travels as flag, not as alternative).
  let survivors = removeInflammationMetaPattern(firedPatterns);

  // Priority 3a: overload supersedes deficiency.
  const overloadResult = applyOverloadSupersession(survivors);
  survivors = overloadResult.survivors;
  suppressed.push(...overloadResult.suppressed);
  if (overloadResult.traceFired) trace.push('overload_supersedes_deficiency');

  // Priority 3b: ACD supersedes FID when anemic (v0.1.1).
  const acdResult = applyACDSupersession(survivors);
  survivors = acdResult.survivors;
  suppressed.push(...acdResult.suppressed);
  if (acdResult.traceFired) trace.push('acd_supersedes_functional_id_when_anemic');

  // Priority 3c: morphology supersedes iron-replete when microcytic.
  // microcytic_normal_iron_studies + stage_0/1 can co-fire (replete ferritin
  // + microcytic MCV); the morphology read carries the actionable signal.
  const morphologyResult = applyMorphologySupersession(survivors);
  survivors = morphologyResult.survivors;
  suppressed.push(...morphologyResult.suppressed);
  if (morphologyResult.traceFired) {
    trace.push('morphology_supersedes_iron_replete_when_microcytic');
  }

  // Resolve winner.
  let winner: WinnerPatternId;
  let secondaryCandidates: SecondaryCandidate[] | undefined;

  if (survivors.length === 0) {
    // Two distinct "no pattern fired" cases. The split is on completeness:
    //   - tier_1 / tier_2 (incomplete panel): the user can resolve by adding
    //     the missing marker (most often ferritin). We frame this as
    //     user-actionable insufficient_data with a "this test next" prompt.
    //   - tier_3 (full panel, all four core markers present): the published
    //     research literally does not have an established pattern for this
    //     specific combination. We frame this as no_established_pattern with
    //     a "this is a clinical-judgment call, not an algorithmic one"
    //     prompt. Pattern emits at low confidence by definition.
    if (completeness === 'tier_3_full_panel') {
      winner = 'no_established_pattern';
      trace.push('resolved_to_no_established_pattern');
    } else {
      winner = 'insufficient_data';
      trace.push('resolved_to_insufficient_data');
    }
  } else if (survivors.length === 1) {
    winner = survivors[0] as PatternId;
    trace.push(`resolved_to_${winner}`);
  } else {
    winner = 'mixed_picture_multiple_patterns';
    trace.push('resolved_to_mixed_picture');
    secondaryCandidates = survivors
      .map((id) => buildSecondaryCandidate(id, inputs))
      // Sort highest-weight first; ties fall back to engine emit order.
      .sort((a, b) => b.weight - a.weight);
  }

  // Priority 5: confidence tier. Uses the combined ferritin caveat so the
  // confidence downgrade fires regardless of whether the cause is
  // inflammation or recent supplementation. Also detects engine-inferred
  // winners (defensible-but-not-corpus-mandated calls) and drops confidence
  // one tier in those cases.
  const isInferential = detectInferentialWinner(winner, evaluation, inputs, survivors.length);
  if (isInferential) trace.push('inferential_winner_confidence_downgraded');
  const confidenceTier = computeConfidenceTier(winner, completeness, ferritinCaveat, isInferential);
  trace.push(`confidence_${confidenceTier}`);

  return {
    winner,
    severity,
    inflammationCaveat,
    supplementationCaveat,
    confidenceTier,
    suppressed,
    arbitrationTrace: trace,
    secondaryCandidates,
    completeness,
  };
}
